import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Dimension } from 'vscode/vscode/vs/base/browser/dom';
import { SimpleCompletionItem } from '../../../../services/suggest/browser/simpleCompletionItem.js';
import { LineContext, SimpleCompletionModel } from '../../../../services/suggest/browser/simpleCompletionModel.js';
import { SimpleSuggestWidget } from '../../../../services/suggest/browser/simpleSuggestWidget.js';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { Emitter, Event } from 'vscode/vscode/vs/base/common/event';
import { Disposable, MutableDisposable, combinedDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { editorSuggestWidgetSelectedBackground } from 'vscode/vscode/vs/editor/contrib/suggest/browser/suggestWidget';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import 'vscode/vscode/vs/platform/theme/common/colorUtils';
import { activeContrastBorder } from 'vscode/vscode/vs/platform/theme/common/colors/baseColors';
import 'vscode/vscode/vs/platform/theme/common/colors/chartsColors';
import 'vscode/vscode/vs/platform/theme/common/colors/editorColors';
import 'vscode/vscode/vs/platform/theme/common/colors/inputColors';
import 'vscode/vscode/vs/platform/theme/common/colors/listColors';
import 'vscode/vscode/vs/platform/theme/common/colors/menuColors';
import 'vscode/vscode/vs/platform/theme/common/colors/minimapColors';
import 'vscode/vscode/vs/platform/theme/common/colors/miscColors';
import 'vscode/vscode/vs/platform/theme/common/colors/quickpickColors';
import 'vscode/vscode/vs/platform/theme/common/colors/searchColors';
import { getListStyles } from 'vscode/vscode/vs/platform/theme/browser/defaultStyles';

const pwshTypeToIconMap = {
    0: Codicon.symbolText,
    1: Codicon.history,
    2: Codicon.symbolMethod,
    3: Codicon.symbolFile,
    4: Codicon.folder,
    5: Codicon.symbolProperty,
    6: Codicon.symbolMethod,
    7: Codicon.symbolVariable,
    8: Codicon.symbolValue,
    9: Codicon.symbolVariable,
    10: Codicon.symbolNamespace,
    11: Codicon.symbolInterface,
    12: Codicon.symbolKeyword,
    13: Codicon.symbolKeyword
};
let SuggestAddon = class SuggestAddon extends Disposable {
    constructor(_capabilities, _terminalSuggestWidgetVisibleContextKey, _instantiationService) {
        super();
        this._capabilities = _capabilities;
        this._terminalSuggestWidgetVisibleContextKey = _terminalSuggestWidgetVisibleContextKey;
        this._instantiationService = _instantiationService;
        this._promptInputModelSubscriptions = this._register(( new MutableDisposable()));
        this._enableWidget = true;
        this._cursorIndexDelta = 0;
        this._onBell = this._register(( new Emitter()));
        this.onBell = this._onBell.event;
        this._onAcceptedCompletion = this._register(( new Emitter()));
        this.onAcceptedCompletion = this._onAcceptedCompletion.event;
        this._cachedBashAliases = ( new Set());
        this._cachedBashBuiltins = ( new Set());
        this._cachedBashCommands = ( new Set());
        this._cachedBashKeywords = ( new Set());
        this._register(Event.runAndSubscribe(Event.any(this._capabilities.onDidAddCapabilityType, this._capabilities.onDidRemoveCapabilityType), () => {
            const commandDetection = this._capabilities.get(2 );
            if (commandDetection) {
                if (this._promptInputModel !== commandDetection.promptInputModel) {
                    this._promptInputModel = commandDetection.promptInputModel;
                    this._promptInputModelSubscriptions.value = combinedDisposable(this._promptInputModel.onDidChangeInput(e => this._sync(e)), this._promptInputModel.onDidFinishInput(() => this.hideSuggestWidget()));
                }
            }
            else {
                this._promptInputModel = undefined;
            }
        }));
    }
    activate(xterm) {
        this._terminal = xterm;
        this._register(xterm.parser.registerOscHandler(633 , data => {
            return this._handleVSCodeSequence(data);
        }));
    }
    setPanel(panel) {
        this._panel = panel;
    }
    setScreen(screen) {
        this._screen = screen;
    }
    _sync(promptInputState) {
        this._mostRecentPromptInputState = promptInputState;
        if (!this._promptInputModel || !this._terminal || !this._suggestWidget || !this._initialPromptInputState) {
            return;
        }
        this._currentPromptInputState = promptInputState;
        if (this._currentPromptInputState.cursorIndex < this._initialPromptInputState.cursorIndex) {
            this.hideSuggestWidget();
            return;
        }
        if (this._terminalSuggestWidgetVisibleContextKey.get()) {
            const inputBeforeCursor = this._currentPromptInputState.value.substring(0, this._currentPromptInputState.cursorIndex);
            this._cursorIndexDelta = this._currentPromptInputState.cursorIndex - this._initialPromptInputState.cursorIndex;
            this._suggestWidget.setLineContext(( new LineContext(inputBeforeCursor, this._cursorIndexDelta)));
        }
        if (!this._suggestWidget.hasCompletions()) {
            this.hideSuggestWidget();
            return;
        }
        const dimensions = this._getTerminalDimensions();
        if (!dimensions.width || !dimensions.height) {
            return;
        }
        const xtermBox = this._screen.getBoundingClientRect();
        const panelBox = this._panel.offsetParent.getBoundingClientRect();
        this._suggestWidget.showSuggestions(0, false, false, {
            left: (xtermBox.left - panelBox.left) + this._terminal.buffer.active.cursorX * dimensions.width,
            top: (xtermBox.top - panelBox.top) + this._terminal.buffer.active.cursorY * dimensions.height,
            height: dimensions.height
        });
    }
    _handleVSCodeSequence(data) {
        if (!this._terminal) {
            return false;
        }
        const [command, ...args] = data.split(';');
        switch (command) {
            case "Completions" :
                this._handleCompletionsSequence(this._terminal, data, command, args);
                return true;
            case "CompletionsBash" :
                this._handleCompletionsBashSequence(this._terminal, data, command, args);
                return true;
            case "CompletionsBashFirstWord" :
                return this._handleCompletionsBashFirstWordSequence(this._terminal, data, command, args);
        }
        return false;
    }
    _handleCompletionsSequence(terminal, data, command, args) {
        if (!terminal.element || !this._enableWidget) {
            return;
        }
        const replacementIndex = parseInt(args[0]);
        const replacementLength = parseInt(args[1]);
        if (!args[3]) {
            this._onBell.fire();
            return;
        }
        let completionList = JSON.parse(data.slice(command.length + args[0].length + args[1].length + args[2].length + 4 ));
        if (!Array.isArray(completionList)) {
            completionList = [completionList];
        }
        const completions = ( completionList.map((e) => {
            return ( new SimpleCompletionItem({
                label: e.CompletionText,
                icon: pwshTypeToIconMap[e.ResultType],
                detail: e.ToolTip
            }));
        }));
        this._leadingLineContent = completions[0].completion.label.slice(0, replacementLength);
        this._cursorIndexDelta = 0;
        const model = ( new SimpleCompletionModel(completions, ( new LineContext(this._leadingLineContent, replacementIndex)), replacementIndex, replacementLength));
        if (completions.length === 1) {
            const insertText = completions[0].completion.label.substring(replacementLength);
            if (insertText.length === 0) {
                this._onBell.fire();
                return;
            }
        }
        this._handleCompletionModel(model);
    }
    _handleCompletionsBashFirstWordSequence(terminal, data, command, args) {
        const type = args[0];
        const completionList = data.slice(command.length + type.length + 2 ).split(';');
        let set;
        switch (type) {
            case 'alias':
                set = this._cachedBashAliases;
                break;
            case 'builtin':
                set = this._cachedBashBuiltins;
                break;
            case 'command':
                set = this._cachedBashCommands;
                break;
            case 'keyword':
                set = this._cachedBashKeywords;
                break;
            default: return false;
        }
        set.clear();
        const distinctLabels = ( new Set());
        for (const label of completionList) {
            distinctLabels.add(label);
        }
        for (const label of distinctLabels) {
            set.add(( new SimpleCompletionItem({
                label,
                icon: Codicon.symbolString,
                detail: type
            })));
        }
        this._cachedFirstWord = undefined;
        return true;
    }
    _handleCompletionsBashSequence(terminal, data, command, args) {
        if (!terminal.element) {
            return;
        }
        let replacementIndex = parseInt(args[0]);
        const replacementLength = parseInt(args[1]);
        if (!args[2]) {
            this._onBell.fire();
            return;
        }
        const completionList = data.slice(command.length + args[0].length + args[1].length + args[2].length + 4 ).split(';');
        let completions;
        if (replacementIndex !== 100 && completionList.length > 0) {
            completions = ( completionList.map(label => {
                return ( new SimpleCompletionItem({
                    label: label,
                    icon: Codicon.symbolProperty
                }));
            }));
        }
        else {
            replacementIndex = 0;
            if (!this._cachedFirstWord) {
                this._cachedFirstWord = [
                    ...this._cachedBashAliases,
                    ...this._cachedBashBuiltins,
                    ...this._cachedBashCommands,
                    ...this._cachedBashKeywords
                ];
                this._cachedFirstWord.sort((a, b) => {
                    const aCode = a.completion.label.charCodeAt(0);
                    const bCode = b.completion.label.charCodeAt(0);
                    const isANonAlpha = aCode < 65 || aCode > 90 && aCode < 97 || aCode > 122 ? 1 : 0;
                    const isBNonAlpha = bCode < 65 || bCode > 90 && bCode < 97 || bCode > 122 ? 1 : 0;
                    if (isANonAlpha !== isBNonAlpha) {
                        return isANonAlpha - isBNonAlpha;
                    }
                    return a.completion.label.localeCompare(b.completion.label);
                });
            }
            completions = this._cachedFirstWord;
        }
        if (completions.length === 0) {
            return;
        }
        this._leadingLineContent = completions[0].completion.label.slice(0, replacementLength);
        const model = ( new SimpleCompletionModel(completions, ( new LineContext(this._leadingLineContent, replacementIndex)), replacementIndex, replacementLength));
        if (completions.length === 1) {
            const insertText = completions[0].completion.label.substring(replacementLength);
            if (insertText.length === 0) {
                this._onBell.fire();
                return;
            }
        }
        this._handleCompletionModel(model);
    }
    _getTerminalDimensions() {
        return {
            width: this._terminal._core._renderService.dimensions.css.cell.width,
            height: this._terminal._core._renderService.dimensions.css.cell.height,
        };
    }
    _handleCompletionModel(model) {
        if (model.items.length === 0 || !this._terminal?.element || !this._promptInputModel) {
            return;
        }
        const suggestWidget = this._ensureSuggestWidget(this._terminal);
        const dimensions = this._getTerminalDimensions();
        if (!dimensions.width || !dimensions.height) {
            return;
        }
        const xtermBox = this._screen.getBoundingClientRect();
        const panelBox = this._panel.offsetParent.getBoundingClientRect();
        this._initialPromptInputState = {
            value: this._promptInputModel.value,
            cursorIndex: this._promptInputModel.cursorIndex,
            ghostTextIndex: this._promptInputModel.ghostTextIndex
        };
        suggestWidget.setCompletionModel(model);
        suggestWidget.showSuggestions(0, false, false, {
            left: (xtermBox.left - panelBox.left) + this._terminal.buffer.active.cursorX * dimensions.width,
            top: (xtermBox.top - panelBox.top) + this._terminal.buffer.active.cursorY * dimensions.height,
            height: dimensions.height
        });
    }
    _ensureSuggestWidget(terminal) {
        this._terminalSuggestWidgetVisibleContextKey.set(true);
        if (!this._suggestWidget) {
            this._suggestWidget = this._register(this._instantiationService.createInstance(SimpleSuggestWidget, this._panel, this._instantiationService.createInstance(PersistedWidgetSize), {}));
            this._suggestWidget.list.style(getListStyles({
                listInactiveFocusBackground: editorSuggestWidgetSelectedBackground,
                listInactiveFocusOutline: activeContrastBorder
            }));
            this._suggestWidget.onDidSelect(async (e) => this.acceptSelectedSuggestion(e));
            this._suggestWidget.onDidHide(() => this._terminalSuggestWidgetVisibleContextKey.set(false));
            this._suggestWidget.onDidShow(() => {
                this._initialPromptInputState = {
                    value: this._promptInputModel.value,
                    cursorIndex: this._promptInputModel.cursorIndex,
                    ghostTextIndex: this._promptInputModel.ghostTextIndex
                };
                this._terminalSuggestWidgetVisibleContextKey.set(true);
            });
        }
        return this._suggestWidget;
    }
    selectPreviousSuggestion() {
        this._suggestWidget?.selectPrevious();
    }
    selectPreviousPageSuggestion() {
        this._suggestWidget?.selectPreviousPage();
    }
    selectNextSuggestion() {
        this._suggestWidget?.selectNext();
    }
    selectNextPageSuggestion() {
        this._suggestWidget?.selectNextPage();
    }
    acceptSelectedSuggestion(suggestion) {
        if (!suggestion) {
            suggestion = this._suggestWidget?.getFocusedItem();
        }
        const initialPromptInputState = this._initialPromptInputState ?? this._mostRecentPromptInputState;
        if (!suggestion || !initialPromptInputState) {
            return;
        }
        this._suggestWidget?.hide();
        const currentPromptInputState = this._currentPromptInputState ?? initialPromptInputState;
        const additionalInput = currentPromptInputState.value.substring(initialPromptInputState.cursorIndex, currentPromptInputState.cursorIndex);
        const initialInput = initialPromptInputState.value.substring(0, initialPromptInputState.cursorIndex);
        const lastSpaceIndex = initialInput.lastIndexOf(' ');
        const finalCompletionRightSide = suggestion.item.completion.label.substring(initialPromptInputState.cursorIndex - (lastSpaceIndex === -1 ? 0 : lastSpaceIndex + 1));
        let finalCompletionLeftSide = suggestion.item.completion.label.substring(0, initialPromptInputState.cursorIndex - (lastSpaceIndex === -1 ? 0 : lastSpaceIndex + 1));
        if (initialInput.endsWith(finalCompletionLeftSide)) {
            finalCompletionLeftSide = '';
        }
        this._onAcceptedCompletion.fire([
            '\x1b[24~y',
            '\x7F'.repeat(additionalInput.length),
            '\x7F'.repeat(finalCompletionLeftSide.length),
            finalCompletionLeftSide,
            finalCompletionRightSide,
            '\x1b[24~z',
        ].join(''));
        this.hideSuggestWidget();
    }
    hideSuggestWidget() {
        this._initialPromptInputState = undefined;
        this._currentPromptInputState = undefined;
        this._suggestWidget?.hide();
    }
};
SuggestAddon = ( __decorate([
    ( __param(2, IInstantiationService))
], SuggestAddon));
let PersistedWidgetSize = class PersistedWidgetSize {
    constructor(_storageService) {
        this._storageService = _storageService;
        this._key = "terminal.integrated.suggestSize" ;
    }
    restore() {
        const raw = this._storageService.get(this._key, 0 ) ?? '';
        try {
            const obj = JSON.parse(raw);
            if (Dimension.is(obj)) {
                return Dimension.lift(obj);
            }
        }
        catch {
        }
        return undefined;
    }
    store(size) {
        this._storageService.store(this._key, JSON.stringify(size), 0 , 1 );
    }
    reset() {
        this._storageService.remove(this._key, 0 );
    }
};
PersistedWidgetSize = ( __decorate([
    ( __param(0, IStorageService))
], PersistedWidgetSize));

export { SuggestAddon };
